/**
 * The new job page (threads/job-page-fresh, Corbin 10/28, direction C):
 * three answers you can read from across the desk — did it pass, whose turn,
 * when and who — then the trail Visit · Completed · City · Customer · Paid.
 *
 * It replaces the old "new" page behind the same per-person switch
 * (`job-page.finish-line`); the classic page stays. The page decides nothing
 * new: devices by the job page's device rule, whose turn by Closeout's rule,
 * the top box's words by the job type (freshModel.ts says where each comes from).
 */
import { useEffect, useMemo, useState } from "react";
import { setJobPageVariant } from "@/lib/api-screen";
import { logPageEscape } from "@/lib/page-escape";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import type { Ball } from "@/lib/closeout-ball";
import { useFinishLineSwitch } from "@/lib/finish-line";
import { FALLBACK_OUTCOMES, tallyOutcomes, type OutcomeSet } from "@/lib/job-outcomes";
import { InfoButton } from "@/components/info-button";
import { OutcomeNumbers } from "@/components/job-outcome-box";
import { buildDevices } from "../detail/calm/calmModel";
import { JobPageVersionToggle } from "../detail/job-page-version-toggle";
import type { JobDetailResponse } from "../detail/types";
import { FreshNotice } from "./fresh-notice";
import { JobPageFeedbackBox, useMarkTriedNew } from "./try-new-page";
import { FRESH_HELP } from "./fresh-help";
import { WhoToCall } from "./who-to-call";
import { FreshDeviceWork } from "./fresh-device-work";
import { FreshPaperwork, PAPERWORK_ANCHOR, scrollToSection } from "./fresh-paperwork";
import { FreshNotes } from "./fresh-notes";
import { TechSees } from "./tech-sees";
import { FreshMore } from "./fresh-more";
import { FreshJobCards, FreshStrips } from "./fresh-strips";
import { FreshStatusMoves, FreshVitals } from "./fresh-header-work";
import { useJobMutations } from "../detail/use-job-mutations";
import { DetailsCard, ScheduleCrewCard, WaterReportingCard, WorkOrderMappingCard } from "../detail/reference-rail";
import { isPostVisit } from "../jobDetailHelpers";
import type { TechOption } from "../detail/types";
import { TaskListInline } from "@/components/task-list-inline";
import { FromTheFieldSection } from "@/components/field-add/field-add-office";
import {
    buildTrail,
    firstQuestion,
    itemStateOf,
    moneyOf,
    phaseOf,
    whoseTurn,
    type Stop,
    type TrailFacts,
    type Turn,
} from "./freshModel";

const CARD = "rounded-2xl border border-border bg-card text-card-foreground";

function dayWords(iso: string | null | undefined) {
    if (!iso) return { day: "Not booked", time: null as string | null };
    const d = new Date(iso);
    return {
        day: d.toLocaleDateString("en-US", {
            weekday: "short",
            month: "short",
            day: "numeric",
            timeZone: "America/Chicago",
        }),
        time: d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/Chicago" }),
    };
}

export default function FreshJobPage() {
    const { id } = useParams<{ id: string }>();
    const { isOffice } = useAuth();
    const sw = useFinishLineSwitch();
    // "Has tried the new page": back on classic, they're asked what was missing (11/4).
    useMarkTriedNew(sw.on);
    // Pilot tracking: every audited call from this page is tagged
    // metadata.screen = 'job_page_fresh' (X-GWC-Screen, utils/auditScreen.ts).
    useEffect(() => {
        setJobPageVariant("fresh");
        return () => setJobPageVariant(null);
    }, []);
    const jobQ = useQuery({
        queryKey: ["job", id],
        queryFn: () => api.get<JobDetailResponse>(`/api/jobs/${id}`),
        enabled: !!id,
    });
    const trailQ = useQuery({
        queryKey: ["job", id, "trail"],
        queryFn: async () =>
            (await api.get<{ trail: TrailFacts & { outcomes: OutcomeSet } }>(`/api/jobs/${id}/trail`)).trail,
        enabled: !!id,
        staleTime: 15_000,
    });
    const job = jobQ.data?.job ?? null;
    const phase = phaseOf(job?.status);
    // The classic page's own job mutations (status, date, tech, crew,
    // confirm, inline details): same code, same dispatch gates.
    const { updateStatus, railMutations, assignmentDialog } = useJobMutations(id, job);
    const [scheduleNonce, setScheduleNonce] = useState(0);
    const openSchedule = () => setScheduleNonce((n) => n + 1);
    const techsQ = useQuery({
        queryKey: ["dispatch", "technicians"],
        queryFn: () => api.get<{ users: TechOption[] }>("/api/dispatch/technicians"),
        staleTime: 5 * 60_000,
        enabled: isOffice,
    });
    const [wantJobTypes, setWantJobTypes] = useState(false);
    const jobTypesQ = useQuery({
        queryKey: ["job-types", "active"],
        queryFn: () =>
            api.get<{ jobTypes: Array<{ id: string; name: string; trade_name?: string | null }> }>(
                "/api/job-types?activeOnly=true",
            ),
        enabled: wantJobTypes,
        staleTime: 5 * 60_000,
    });
    // Closeout's own "whose turn" — same cache key as the hand-off strip.
    const queueQ = useQuery({
        queryKey: ["job-handoff-queue", id],
        queryFn: () =>
            api.get<{ jobs: Array<{ id: string; ball?: Ball | null }> }>(
                `/api/close-out/queue?jobId=${encodeURIComponent(id ?? "")}`,
            ),
        enabled: isOffice && !!id && phase === "closeout",
        retry: false,
        staleTime: 30_000,
    });

    const view = useMemo(() => {
        if (!jobQ.data || !job) return null;
        const postVisit = phase !== "before";
        const devices = buildDevices(
            jobQ.data.assets ?? [],
            jobQ.data.testResults ?? [],
            jobQ.data.repairResults ?? [],
            postVisit,
        );
        const states = devices.map(itemStateOf);
        const set = trailQ.data?.outcomes ?? FALLBACK_OUTCOMES;
        const tally = tallyOutcomes(set, states, !postVisit);
        const problems = devices.filter((_d, i) => ["failed", "open", "unfinished"].includes(states[i]!));
        const money = moneyOf(jobQ.data.invoices ?? []);
        const ball = queueQ.data?.jobs?.[0]?.ball ?? null;
        const turn = whoseTurn({
            job,
            phase,
            ball,
            leftCloseout: !!queueQ.data && (queueQ.data.jobs?.length ?? 0) === 0,
            paid: money.paid || money.none,
        });
        const finished = states.filter((s) => ["passed", "failed", "repaired", "replaced", "done"].includes(s)).length;
        const trail = trailQ.data
            ? buildTrail({
                  jobId: job.id,
                  job,
                  phase,
                  facts: trailQ.data,
                  total: states.length,
                  finished,
                  couldnt: states.filter((s) => s === "couldnt").length,
                  stillOpen: states.filter((s) => s === "open" || s === "unfinished").length,
                  money,
                  mineTopic: ball?.mine ? ball.topic : null,
              })
            : null;
        return { set, tally, problems, turn, trail, wordsLoaded: !!trailQ.data };
    }, [jobQ.data, job, phase, trailQ.data, queueQ.data]);

    if (jobQ.isLoading) {
        return <div className="p-6 text-shell-foreground/80">Loading the job…</div>;
    }
    if (!job || !view) {
        return (
            <div className={cn(CARD, "m-4 p-6")}>
                <p className="font-semibold">This job couldn't be loaded.</p>
                <Link to="/jobs" className="text-primary underline">
                    Back to jobs
                </Link>
            </div>
        );
    }
    const when = dayWords(job.arrived_at ?? job.scheduled_start);
    const crew = (job.crew ?? []).map((c) => c.name).filter(Boolean) as string[];

    return (
        <div className="mx-auto grid max-w-[1180px] gap-3 pb-10" data-testid="fresh-job-page">
            <FreshNotice mode="fresh" />
            <header className={cn(CARD, "grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3 px-5 py-4")}>
                <div className="min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        <Link
                            to={`/customers/${job.customer_id}`}
                            className="text-[26px] font-extrabold leading-tight tracking-tight hover:underline"
                            data-testid="fresh-customer"
                        >
                            {job.company_name}
                        </Link>
                        {job.site_id && (
                            <Link
                                to={`/sites/${job.site_id}`}
                                className="text-[17px] text-muted-foreground hover:underline"
                            >
                                {[job.site_name, job.site_city].filter(Boolean).join(" · ") || "Site"}
                            </Link>
                        )}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-3 text-[15px] text-muted-foreground">
                        <span>{job.job_type_name ?? "Job"}</span>
                        <button
                            type="button"
                            className="font-mono hover:text-foreground"
                            title="Copy the job number"
                            onClick={() => {
                                void navigator.clipboard
                                    ?.writeText(job.display_number || job.id)
                                    .then(() => toast.success("Job number copied"));
                            }}
                        >
                            #{job.display_number || job.id.slice(0, 8)}
                        </button>
                    </div>
                    <FreshVitals data={jobQ.data!} />
                    {isOffice && (
                        <div className="mt-3">
                            <FreshStatusMoves
                                data={jobQ.data!}
                                updateStatus={updateStatus}
                                onNeedSchedule={openSchedule}
                            />
                        </div>
                    )}
                </div>
                {sw.allowed && (
                    <JobPageVersionToggle
                        isNew
                        onChange={(on) => {
                            // Leaving for the classic page: log it on this job too.
                            if (!on) logPageEscape("job_page_fresh", "version-toggle", job.id);
                            sw.set(on);
                        }}
                    />
                )}
            </header>
            <div data-testid="fresh-feedback" className={cn(CARD, "px-5 py-3")}>
                <JobPageFeedbackBox jobId={job.id} page="new" />
            </div>

            <FreshStrips job={job} />

            <section className="grid gap-3 lg:grid-cols-[1.15fr_1.25fr_1fr]" aria-label="The three answers">
                <div className={cn(CARD, "p-4")} data-testid="fresh-counts">
                    <div className="flex items-center justify-between text-[15px] font-bold text-muted-foreground">
                        {firstQuestion(view.set, phase)}
                        <InfoButton topic={FRESH_HELP.counts} />
                    </div>
                    {view.wordsLoaded ? (
                        <OutcomeNumbers tally={view.tally} set={view.set} />
                    ) : (
                        <div className="mt-2 text-[16px] text-muted-foreground">{view.tally.total} on this job</div>
                    )}
                    {view.problems.length > 0 && (
                        <ul className="m-0 mt-2 list-none space-y-0.5 p-0 text-[14.5px]" data-testid="fresh-problems">
                            {view.problems.slice(0, 3).map((d) => (
                                <li key={d.asset.link_id}>
                                    <span
                                        className={
                                            d.tally === "failed" && (d.asset.required_action || "test") === "test"
                                                ? "font-bold text-red-700 dark:text-red-400"
                                                : "font-bold text-amber-700 dark:text-amber-400"
                                        }
                                    >
                                        {d.word.replace(/^✓\s*/, "")}
                                    </span>{" "}
                                    {[d.asset.size_label, d.asset.manufacturer, d.asset.model]
                                        .filter(Boolean)
                                        .join(" ")}
                                    {d.asset.location_description ? `, ${d.asset.location_description}` : ""}
                                </li>
                            ))}
                            {view.problems.length > 3 && (
                                <li className="text-muted-foreground">and {view.problems.length - 3} more</li>
                            )}
                        </ul>
                    )}
                    {view.tally.unfinished > 0 && (
                        <p className="mt-1 text-[14px] font-semibold text-red-700 dark:text-red-400">
                            {view.tally.unfinished} test{view.tally.unfinished === 1 ? "" : "s"} not finished
                        </p>
                    )}
                </div>
                <TurnBox turn={view.turn} />
                <div className={cn(CARD, "p-4")} data-testid="fresh-when">
                    <div className="flex items-center justify-between text-[15px] font-bold text-muted-foreground">
                        When &amp; who
                        <InfoButton topic={FRESH_HELP.when} />
                    </div>
                    <div className="mt-2 text-[24px] font-extrabold leading-tight">{when.day}</div>
                    {when.time && <div className="text-muted-foreground">{when.time}</div>}
                    <div className="mt-1.5 text-[16px]">
                        {[job.assigned_to_name, ...crew].filter(Boolean).join(" + ") || "No tech yet"}
                    </div>
                    {isOffice && (
                        <button
                            type="button"
                            className="mt-2 inline-flex min-h-[40px] items-center rounded-lg border border-border px-3 font-semibold hover:bg-muted"
                            onClick={openSchedule}
                            data-testid="fresh-change-schedule"
                        >
                            Change date, tech or crew
                        </button>
                    )}
                </div>
            </section>

            <WhoToCall job={job} phase={phase} />

            {view.trail && <TrailRow stops={view.trail} />}

            {/* Each column is its own one-track grid capped at its width
                (minmax(0,1fr) + min-w-0). Without it the implicit track grows to
                the widest child — a long file name, the photo row — and the left
                column slides under the right one. */}
            <div className="grid gap-3 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
                <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] content-start gap-3">
                    <FreshDeviceWork data={jobQ.data!} onUploadReport={() => scrollToSection(PAPERWORK_ANCHOR)} />
                    <TechSees jobId={job.id} jobStatus={job.status} />
                    <FreshPaperwork data={jobQ.data!} />
                    <FreshJobCards job={job} />
                </div>
                <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] content-start gap-3">
                    {isOffice && (
                        <div id="fresh-schedule" className="scroll-mt-4" data-testid="fresh-schedule">
                            <ScheduleCrewCard
                                job={job}
                                postVisit={isPostVisit(job.status)}
                                techs={techsQ.data?.users ?? []}
                                techsLoading={techsQ.isLoading}
                                m={railMutations}
                                editNonce={scheduleNonce}
                            />
                        </div>
                    )}
                    <FreshNotes job={job} />
                    {isOffice && (
                        <div className="grid gap-3" data-testid="fresh-details">
                            <DetailsCard
                                job={job}
                                jobTypes={jobTypesQ.data?.jobTypes ?? []}
                                jobTypesLoading={jobTypesQ.isLoading}
                                onWantJobTypes={() => setWantJobTypes(true)}
                                m={railMutations}
                            />
                            {jobQ.data?.workOrder && <WorkOrderMappingCard wo={jobQ.data.workOrder} />}
                            <WaterReportingCard job={job} />
                        </div>
                    )}
                    <TaskListInline entityType="job" entityId={job.id} />
                    <FromTheFieldSection kind="job" id={job.id} />
                </div>
            </div>
            <FreshMore job={job} />
            {assignmentDialog}
        </div>
    );
}

const TURN_TONE: Record<Turn["tone"], string> = {
    mine: "border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10",
    waiting: "",
    done: "border-emerald-300 bg-emerald-50 dark:border-emerald-500/40 dark:bg-emerald-500/10",
    stop: "border-red-400 bg-red-50 dark:border-red-500/50 dark:bg-red-500/10",
};

function TurnBox({ turn }: { turn: Turn }) {
    return (
        <div className={cn(CARD, "p-4", TURN_TONE[turn.tone])} data-testid="fresh-turn" data-tone={turn.tone}>
            <div className="flex items-center justify-between text-[15px] font-bold text-muted-foreground">
                Whose turn?
                <InfoButton topic={FRESH_HELP.turn} />
            </div>
            <p className="mb-1 mt-2 text-[21px] font-bold leading-snug text-foreground">{turn.text}</p>
            {turn.sub && <p className="mb-2 text-[15px] text-muted-foreground">{turn.sub}</p>}
            {turn.action && (
                <Link
                    to={turn.action.to}
                    className="inline-flex min-h-[44px] items-center rounded-lg bg-primary px-4 font-bold text-primary-foreground hover:bg-primary/90"
                    data-testid="fresh-turn-action"
                >
                    {turn.action.label}
                </Link>
            )}
        </div>
    );
}

const STOP_ICON: Record<Stop["state"], { mark: string; cls: string }> = {
    done: { mark: "✓", cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300" },
    mine: { mark: "!", cls: "bg-amber-100 text-amber-900 dark:bg-amber-500/20 dark:text-amber-300" },
    waiting: { mark: "◷", cls: "bg-muted text-muted-foreground" },
    later: { mark: "○", cls: "bg-muted text-muted-foreground" },
};

function TrailRow({ stops }: { stops: Stop[] }) {
    return (
        <section className={cn(CARD, "relative overflow-hidden")} aria-label="The trail" data-testid="fresh-trail">
            <div className="absolute right-2 top-2 z-10">
                <InfoButton topic={FRESH_HELP.trail} />
            </div>
            <ol
                className="m-0 grid list-none p-0"
                style={{ gridTemplateColumns: `repeat(auto-fit, minmax(170px, 1fr))` }}
            >
                {stops.map((s) => (
                    <li
                        key={s.key}
                        className={cn(
                            "grid content-start gap-1 border-t border-border px-4 py-3 sm:border-l sm:border-t-0",
                            s.state === "mine" && "bg-amber-50 dark:bg-amber-500/10",
                        )}
                        data-testid={`fresh-stop-${s.key}`}
                        data-state={s.state}
                    >
                        <span className="flex items-center gap-2 font-bold">
                            <span
                                className={cn(
                                    "grid h-7 w-7 place-items-center rounded-full text-[13px] font-extrabold",
                                    STOP_ICON[s.state].cls,
                                )}
                                aria-hidden
                            >
                                {STOP_ICON[s.state].mark}
                            </span>
                            {s.name}
                        </span>
                        <span className="text-[14.5px] text-muted-foreground">{s.words}</span>
                        {s.action && (
                            <Link
                                to={s.action.to}
                                className="mt-1 inline-flex min-h-[40px] w-fit items-center rounded-lg bg-primary px-3 text-[14px] font-bold text-primary-foreground hover:bg-primary/90"
                            >
                                {s.action.label}
                            </Link>
                        )}
                    </li>
                ))}
            </ol>
        </section>
    );
}
